import Debug from '../utils/Debug.js';
import { getFileIndex } from './LPCLayerDefinitions.js';

/**
 * LPCCompositor - Composites layered LPC spritesheets into a single SpriteAtlas
 *
 * Takes a stack of layer definitions (body base + equipment layers), loads their
 * per-animation PNG sheets, composites them by zPos order, and produces a
 * SpriteAtlas-compatible object for use with CharacterSprite.
 *
 * LPC sheets are per-animation PNGs:
 *   Width = numFrames * 64
 *   Height = 4 * 64 (4 directions: south, west, east, north)
 *
 * Trixel uses 8 directions — diagonal directions are generated by mirroring:
 *   south-east = south (or mirror of south-west)
 *   south-west = mirror of south-east direction (mirror of south row)
 *   north-east = mirror of north-west (mirror of north row)
 *   north-west = north-west (or mirror)
 *
 * Strategy: East row = horizontal flip of West row (LPC convention).
 * For diagonals, we use the closest cardinal direction.
 */

const FRAME_SIZE = 64;
const LPC_EXT = '.webp';

// LPC direction rows (top to bottom in spritesheet): north, west, south, east
const LPC_DIR_ROWS = { north: 0, west: 1, south: 2, east: 3 };

// Map trixel 8-direction to LPC source direction + whether to flip.
// LPC only has 4 directions; diagonals use the nearest cardinal direction.
// For best visual variety, NE/NW use the adjacent side profile (east/west)
// while SE/SW use the front-facing south row (with flip for SW).
const DIRECTION_MAP = {
  'south':      { lpcDir: 'south', flip: false },
  'south-east': { lpcDir: 'east',  flip: false },
  'east':       { lpcDir: 'east',  flip: false },
  'north-east': { lpcDir: 'north', flip: false },
  'north':      { lpcDir: 'north', flip: false },
  'north-west': { lpcDir: 'north', flip: true  },
  'west':       { lpcDir: 'west',  flip: false },
  'south-west': { lpcDir: 'west',  flip: false }
};

// Trixel animation name → LPC animation name
const TRIXEL_TO_LPC_ANIM = {
  'walking-8-frames':          'walk',
  'walk-6-frames':             'walk',
  'breathing-idle':            'idle',
  'fight-stance-idle-8-frames':'idle',
  'idle':                      'idle',
  'idle-long':                 'idle',
  'running-8-frames':          'run',
  'running-6-frames':          'run',
  'fireball':                  'spellcast',
  'surprise-uppercut':         'slash',
  'hurricane-kick':            'slash',
  'lead-jab':                  'thrust',
  'roundhouse-kick':           'slash',
  'high-kick':                 'slash',
  'leg-sweep':                 'slash',
  'cross-punch':               'thrust',
  'flying-kick':               'slash',
  'jumping':                   'jump',
  'jumping-1':                 'jump',
  'jumping-2':                 'jump',
  'taking-punch':              'hurt',
  'falling-back-death':        'hurt',
  'drinking':                  'idle',
  'picking-up':                'idle',
  'eating':                    'idle',
  'going-to-sleep':            'sit',
  'jump':                      'jump',
  // Weapon-specific attack animations (mapped per-weapon via attackAnims)
  'weapon-slash':              'slash',
  'weapon-slash-reverse':      'slash_reverse',
  'weapon-thrust':             'thrust',
  'weapon-backslash':          'backslash',
  'weapon-halfslash':          'halfslash',
  'weapon-shoot':              'shoot'
};

// LPC animation folder name overrides
const LPC_ANIM_FOLDER = {
  'combat':       'combat_idle',
  '1h_slash':     'backslash',
  '1h_backslash': 'backslash',
  '1h_halfslash': 'halfslash'
};

// Fallback animations: when a layer lacks a specific animation, use this instead.
// e.g., child body has no 'run' folder → fall back to 'walk' sprites
const LPC_ANIM_FALLBACK = {
  'run':         'walk',
  'walk':        'idle',   // shields have idle colors but limited walk coverage
  'idle':        'walk',   // weapons/equipment often only have walk
  'jump':        'walk',   // most equipment lacks jump
  'spellcast':   'idle',
  'thrust':      'slash',
  'combat_idle': 'idle',
  'emote':       'idle',
  'climb':       'walk',
  'shoot':       'thrust',
  'backslash':      'slash',
  'halfslash':      'slash',
  'slash_reverse':  'slash',
  'sit':            'idle'
};

// LPC animation frame counts (max frames per direction)
const LPC_FRAME_COUNTS = {
  'spellcast': 7, 'thrust': 8, 'walk': 9, 'slash': 6, 'shoot': 13,
  'hurt': 6, 'climb': 6, 'idle': 2, 'jump': 5, 'sit': 3,
  'emote': 3, 'run': 8, 'combat_idle': 2, 'backslash': 13, 'halfslash': 6,
  'slash_reverse': 6
};

// Atlas cache (equipment hash → composed atlas)
const _atlasCache = new Map();
const MAX_CACHE_SIZE = 20;

// Individual image cache (URL → Promise<HTMLImageElement>)
// Prevents redundant fetches when multiple characters share layers (e.g., same hair style)
const _imageCache = new Map();
const MAX_IMAGE_CACHE_SIZE = 500;


/**
 * @typedef {Object} LayerDef
 * @property {string} basePath - Path to layer spritesheets dir (e.g., '/lpc/spritesheets/torso/armour/plate/male')
 * @property {number} zPos - Z-position for draw ordering
 * @property {string[]} [animations] - Supported LPC animation names
 * @property {string} variant - Variant name (e.g., 'steel', 'light')
 */

export default class LPCCompositor {

  /**
   * Compose a layered character atlas from multiple LPC sprite layers
   * @param {LayerDef[]} layers - Ordered array of layer definitions
   * @param {string[]} trixelAnimations - Trixel animation names to include
   * @returns {Promise<LPCAtlas>} SpriteAtlas-compatible object
   */
  static async compose(layers, trixelAnimations) {
    // Generate cache key from layer definitions + animations
    const cacheKey = LPCCompositor._getCacheKey(layers, trixelAnimations);

    // Check cache
    if (_atlasCache.has(cacheKey)) {
      Debug.log('LPCCompositor', 'Cache hit for equipment combo');
      return _atlasCache.get(cacheKey);
    }

    Debug.log('LPCCompositor', `Composing ${layers.length} layers for ${trixelAnimations.length} animations`);

    // Sort layers by zPos for correct draw order
    const sortedLayers = [...layers].sort((a, b) => a.zPos - b.zPos);

    // Determine which LPC animations we need
    const lpcAnimsNeeded = new Set();
    for (const trixelAnim of trixelAnimations) {
      const lpcAnim = TRIXEL_TO_LPC_ANIM[trixelAnim] || trixelAnim;
      lpcAnimsNeeded.add(lpcAnim);
    }

    // Load all layer images for all needed animations.
    // bodyFallbacks: Map of lpcAnim → sourceAnim when body used a different sheet
    // (e.g., skeleton has no idle → falls back to walk)
    const { imageMap: layerImages, bodyFallbacks } =
      await LPCCompositor._loadLayerImages(sortedLayers, lpcAnimsNeeded);

    // Scan loaded images to determine max frame size per LPC animation.
    // Oversize layers (192x192 weapon attacks) produce larger atlas tiles.
    const animTileSizes = {};
    for (const lpcAnim of lpcAnimsNeeded) {
      let maxSize = FRAME_SIZE;
      for (let li = 0; li < sortedLayers.length; li++) {
        const entry = layerImages.get(`${li}_${lpcAnim}`);
        if (!entry) continue;
        const { frameW } = LPCCompositor._getImageFrameSize(entry.img, entry.sourceAnim);
        maxSize = Math.max(maxSize, frameW);
      }
      animTileSizes[lpcAnim] = maxSize;
    }

    // Composite into a single atlas canvas
    const composited = LPCCompositor._compositeAtlas(
      sortedLayers, layerImages, trixelAnimations, lpcAnimsNeeded, animTileSizes,
      bodyFallbacks
    );

    // Convert canvas to blob URL (avoids data URL size limits that cause
    // invisible characters with large oversize atlases)
    const blobUrl = await new Promise((resolve, reject) => {
      composited.canvas.toBlob(blob => {
        if (blob) resolve(URL.createObjectURL(blob));
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/webp', 1.0);
    });

    const atlas = new LPCAtlas({
      url: blobUrl,
      canvas: null, // Already converted to blob — no need to keep canvas
      width: composited.width,
      height: composited.height,
      frameWidth: FRAME_SIZE,
      frameHeight: FRAME_SIZE,
      cols: 0,
      rows: 0,
      rotationLookup: composited.rotationLookup,
      animationLookup: composited.animationLookup,
      bodyFrameSize: FRAME_SIZE,
      hasOversize: composited.hasOversize
    });

    // Cache the result
    if (_atlasCache.size >= MAX_CACHE_SIZE) {
      // Evict oldest entry — but don't destroy if sprites still reference it.
      // The atlas will be destroyed when the last sprite releases it.
      const oldestKey = _atlasCache.keys().next().value;
      const oldAtlas = _atlasCache.get(oldestKey);
      if (oldAtlas) {
        oldAtlas._inCache = false;
        if (oldAtlas._refCount <= 0) {
          oldAtlas.destroy();
        }
      }
      _atlasCache.delete(oldestKey);
    }
    atlas._inCache = true;
    _atlasCache.set(cacheKey, atlas);

    return atlas;
  }

  /**
   * Load all animation sheet images for all layers
   */
  static async _loadLayerImages(layers, lpcAnims) {
    const imageMap = new Map(); // `${layerIndex}_${lpcAnim}` → { img, sourceAnim }

    const loadPromises = [];

    // File index: path → { a: anims[], v: variants[], x: exceptions, f: flatAnims[], w: rootVariants[] }
    const fileIndex = getFileIndex();

    for (let li = 0; li < layers.length; li++) {
      const layer = layers[li];
      const v = layer.variant;
      const bp = layer.basePath;
      // Strip the LPC_BASE prefix to get the relative path for file index lookup
      const relPath = bp.replace(/^\/lpc\/spritesheets\//, '');
      const fi = fileIndex ? fileIndex[relPath] : null;

      for (const lpcAnim of lpcAnims) {
        // Weapon layers with customAnimation are restricted to specific LPC anims
        if (layer.animations && layer.animations.length > 0) {
          if (!layer.animations.includes(lpcAnim)) continue;
        }

        const animFolder = LPC_ANIM_FOLDER[lpcAnim] || lpcAnim;
        const key = `${li}_${lpcAnim}`;

        // Resolve the exact path using the file index — no guessing
        const resolved = LPCCompositor._resolvePathFromIndex(
          fi, bp, v, lpcAnim, animFolder, layer
        );

        if (!resolved) continue;

        const promise = LPCCompositor._loadImage(resolved.path)
          .then(img => { imageMap.set(key, { img, sourceAnim: resolved.sourceAnim }); })
          .catch(() => { /* file missing — skip layer for this anim */ });

        loadPromises.push(promise);
      }
    }

    await Promise.all(loadPromises);

    // Synchronize fallback animations across layers.
    // When the body layer used a fallback animation (e.g., skeleton body has
    // no idle → falls back to walk), force other layers to use the same
    // fallback so all layers animate from matching poses.
    // Find the body layer index (tagged with isBody in buildLayerStack)
    const bodyIdx = layers.findIndex(l => l.isBody);
    const bodyFallbacks = new Map(); // lpcAnim → actual sourceAnim when body used fallback
    const syncPromises = [];
    if (bodyIdx >= 0) {
      for (const lpcAnim of lpcAnims) {
        const bodyEntry = imageMap.get(`${bodyIdx}_${lpcAnim}`);
        if (!bodyEntry || bodyEntry.sourceAnim === lpcAnim) continue;

        const bodySourceAnim = bodyEntry.sourceAnim;
        bodyFallbacks.set(lpcAnim, bodySourceAnim);
        const bodyFolder = LPC_ANIM_FOLDER[bodySourceAnim] || bodySourceAnim;

        for (let li = 0; li < layers.length; li++) {
          if (li === bodyIdx) continue;
          const key = `${li}_${lpcAnim}`;
          const entry = imageMap.get(key);
          if (!entry || entry.sourceAnim === bodySourceAnim) continue;

          // This layer loaded a different animation than the body.
          // Try to load the body's fallback animation for this layer instead,
          // but only if the file index confirms it exists.
          const layer = layers[li];
          const syncRelPath = layer.basePath.replace(/^\/lpc\/spritesheets\//, '');
          const syncFi = fileIndex ? fileIndex[syncRelPath] : null;
          const syncVariant = layer.variant;
          let syncExists = false;
          if (syncFi && syncFi.a && syncFi.a.includes(bodyFolder)) {
            if (syncFi.v && syncFi.v.includes(syncVariant)) {
              syncExists = !(syncFi.x && syncFi.x[bodyFolder] && syncFi.x[bodyFolder].includes('-' + syncVariant));
            } else if (syncFi.x && syncFi.x[bodyFolder]) {
              syncExists = syncFi.x[bodyFolder].includes('+' + syncVariant);
            }
          }
          if (syncExists) {
            syncPromises.push(
              LPCCompositor._loadImage(`${layer.basePath}/${bodyFolder}/${syncVariant}${LPC_EXT}`)
                .then(img => { imageMap.set(key, { img, sourceAnim: bodySourceAnim }); })
                .catch(() => { /* keep existing entry */ })
            );
          }
        }
      }
    }
    if (syncPromises.length > 0) await Promise.all(syncPromises);

    // Generate missing run animations for capes and shoulders.
    // These LPC layers have no native run spritesheet — we synthesize one
    // from the walk sheet with proportional frame mapping and a Y offset
    // so the cape/shoulder aligns with the body's run pose.
    // When LPC releases proper run sheets, they'll load directly and this won't trigger.
    for (let li = 0; li < layers.length; li++) {
      const runKey = `${li}_run`;
      const entry = imageMap.get(runKey);
      if (!entry || entry.sourceAnim !== 'walk') continue;

      const bp = layers[li].basePath || '';
      // Match cape body layers (cape/solid, cape/solid_behind, cape/tattered, etc.)
      // but NOT buckle/collar layers (neck/capeclip, neck/capetie)
      const isCape = /\/cape\//.test(bp);
      const isShoulder = bp.includes('shoulder');
      if (!isCape && !isShoulder) continue;

      const walkImg = entry.img;
      const { frameW, frameH, frameCount: walkFrames } =
        LPCCompositor._getImageFrameSize(walkImg, 'walk');
      const runFrames = LPC_FRAME_COUNTS['run'] || 8;

      const genCanvas = document.createElement('canvas');
      genCanvas.width = runFrames * frameW;
      genCanvas.height = 4 * frameH;
      const gCtx = genCanvas.getContext('2d');
      gCtx.imageSmoothingEnabled = false;

      for (let dir = 0; dir < 4; dir++) {
        // Per-direction offsets to align cape/shoulder with body's run pose
        let dirXOff = 0, dirYOff = 0;
        if (dir === LPC_DIR_ROWS['north']) {
          // Back view: nudge up
          dirYOff = isCape ? -2 : -1;
        } else if (dir === LPC_DIR_ROWS['east']) {
          // Running right: cape trails left, shoulders lean right
          dirXOff = isCape ? -3 : 1;
        } else if (dir === LPC_DIR_ROWS['west']) {
          // Running left: cape trails right, shoulders lean left
          dirXOff = isCape ? 3 : -1;
        }
        for (let f = 0; f < runFrames; f++) {
          // Map run frame index proportionally to walk frame index
          const walkFrame = Math.min(
            Math.round(f * (walkFrames - 1) / (runFrames - 1)),
            walkFrames - 1
          );
          gCtx.drawImage(
            walkImg,
            walkFrame * frameW, dir * frameH, frameW, frameH,
            f * frameW + dirXOff, dir * frameH + dirYOff, frameW, frameH
          );
        }
      }

      // Replace the fallback entry with the generated run sheet.
      // Canvas objects work as drawImage sources just like HTMLImageElement.
      imageMap.set(runKey, { img: genCanvas, sourceAnim: 'run' });
    }

    return { imageMap, bodyFallbacks };
  }

  /**
   * Try loading an image from a list of { path, sourceAnim } entries.
   * Returns { img, sourceAnim } for the first successful load.
   */
  static _tryLoadPathEntries(entries, index = 0) {
    if (index >= entries.length) return Promise.resolve(null);
    const { path, sourceAnim } = entries[index];
    return LPCCompositor._loadImage(path)
      .then(img => ({ img, sourceAnim }))
      .catch(() => LPCCompositor._tryLoadPathEntries(entries, index + 1));
  }

  /**
   * Resolve the exact file path for a layer+animation using the file index.
   * Returns { path, sourceAnim } or null if no file exists.
   *
   * File index entry format:
   *   a: string[]  — animation subdirectory names
   *   v: string[]  — variants in ALL anim folders (intersection)
   *   x: object    — exceptions: { folder: ["+extra", "-missing"] }
   *   f: string[]  — flat animation files (animName.webp, no variant subfolder)
   *   w: string[]  — root variant files (variant.webp, for weapon layers)
   */
  static _resolvePathFromIndex(fi, bp, variant, lpcAnim, animFolder, layer) {
    // Animation-restricted layers (weapons): basePath already includes anim folder.
    // The only valid file is basePath/variant.webp — check 'w' (root variants).
    if (layer.animations && layer.animations.length > 0) {
      if (fi && fi.w && fi.w.includes(variant)) {
        return { path: `${bp}/${variant}${LPC_EXT}`, sourceAnim: lpcAnim };
      }
      // No file index or variant not found — skip
      return fi ? null : { path: `${bp}/${variant}${LPC_EXT}`, sourceAnim: lpcAnim };
    }

    if (!fi) return null; // No file index entry → path doesn't exist

    // Helper: check if a variant exists in a specific animation folder
    const hasVariantInFolder = (folder, v) => {
      if (!fi.a || !fi.a.includes(folder)) return false;
      // Check universal variants
      if (fi.v && fi.v.includes(v)) {
        // But check it's not missing in this folder's exceptions
        if (fi.x && fi.x[folder]) {
          return !fi.x[folder].includes('-' + v);
        }
        return true;
      }
      // Check folder-specific extra variants
      if (fi.x && fi.x[folder]) {
        return fi.x[folder].includes('+' + v);
      }
      return false;
    };

    // Helper: check if a flat animation file exists
    const hasFlatAnim = (anim) => fi.f && fi.f.includes(anim);

    // Build fallback chain: anim → fallback → fallback2
    const chain = [{ anim: lpcAnim, folder: animFolder }];
    const fb1 = LPC_ANIM_FALLBACK[lpcAnim];
    if (fb1) {
      chain.push({ anim: fb1, folder: LPC_ANIM_FOLDER[fb1] || fb1 });
      const fb2 = LPC_ANIM_FALLBACK[fb1];
      if (fb2 && fb2 !== lpcAnim) {
        chain.push({ anim: fb2, folder: LPC_ANIM_FOLDER[fb2] || fb2 });
      }
    }

    // Shield idle/run: prefer walk sheet (correct arm positioning)
    if ((lpcAnim === 'idle' || lpcAnim === 'run') && bp.includes('/shield/')) {
      if (hasVariantInFolder('walk', variant)) {
        return { path: `${bp}/walk/${variant}${LPC_EXT}`, sourceAnim: 'walk' };
      }
    }

    // Try subfolder paths: basePath/animFolder/variant.webp
    for (const { anim, folder } of chain) {
      if (hasVariantInFolder(folder, variant)) {
        return { path: `${bp}/${folder}/${variant}${LPC_EXT}`, sourceAnim: anim };
      }
    }

    // Try flat animation files: basePath/animFolder.webp
    for (const { anim, folder } of chain) {
      if (hasFlatAnim(folder)) {
        return { path: `${bp}/${folder}${LPC_EXT}`, sourceAnim: anim };
      }
    }

    // Try root variant file: basePath/variant.webp (e.g., boomerang)
    if (fi.w && fi.w.includes(variant)) {
      return { path: `${bp}/${variant}${LPC_EXT}`, sourceAnim: lpcAnim };
    }

    return null; // No file exists for this layer+animation+variant combo
  }

  /**
   * Composite all loaded layer images into a single atlas canvas.
   * Uses deduplication (trixel anims sharing an LPC anim share frames)
   * and strip-packing for mixed tile sizes (64x64 body + 192x192 oversize attacks).
   */
  static _compositeAtlas(layers, layerImages, trixelAnimations, lpcAnims, animTileSizes, bodyFallbacks) {
    const trixelDirections = [
      'south', 'south-east', 'east', 'north-east',
      'north', 'north-west', 'west', 'south-west'
    ];

    // Frame counts per unique LPC animation.
    // When the body used a fallback (e.g., skeleton idle → walk), use the
    // fallback animation's frame count so all layers animate the full cycle.
    // But never REDUCE the frame count below the canonical count — body fallbacks
    // like shoot→idle (2 frames) must not cap the 13-frame shoot animation.
    const lpcFrameCounts = {};
    for (const lpcAnim of lpcAnims) {
      const effectiveAnim = bodyFallbacks.get(lpcAnim) || lpcAnim;
      const fallbackFc = LPC_FRAME_COUNTS[effectiveAnim] || LPC_FRAME_COUNTS[LPC_ANIM_FOLDER[effectiveAnim]] || 2;
      const canonicalFc = LPC_FRAME_COUNTS[lpcAnim] || LPC_FRAME_COUNTS[LPC_ANIM_FOLDER[lpcAnim]] || 2;
      lpcFrameCounts[lpcAnim] = Math.max(fallbackFc, canonicalFc);
    }

    // Build list of unique frames to draw (deduped by LPC anim).
    // Rotation frames first (always body-sized), then sorted small→large for better packing.
    const frameSlots = [];

    // Rotation frames (idle frame 0 per direction)
    for (const dir of trixelDirections) {
      frameSlots.push({
        lpcAnim: 'idle', direction: dir, frameIdx: 0,
        tileSize: FRAME_SIZE, type: 'rotation'
      });
    }

    // Sort LPC anims: body-sized first, oversize last (better strip-packing)
    const sortedAnims = [...lpcAnims].sort((a, b) =>
      (animTileSizes[a] || FRAME_SIZE) - (animTileSizes[b] || FRAME_SIZE)
    );

    for (const lpcAnim of sortedAnims) {
      const tileSize = animTileSizes[lpcAnim] || FRAME_SIZE;
      const fc = lpcFrameCounts[lpcAnim];
      for (const dir of trixelDirections) {
        for (let f = 0; f < fc; f++) {
          frameSlots.push({
            lpcAnim, direction: dir, frameIdx: f,
            tileSize, type: 'animation'
          });
        }
      }
    }

    // Strip-pack frames into canvas (handles mixed tile sizes)
    const MAX_CANVAS_WIDTH = 4096;
    let currentX = 0, currentY = 0, currentRowHeight = 0;
    let maxWidth = 0;

    for (const slot of frameSlots) {
      const ts = slot.tileSize;
      if (currentX + ts > MAX_CANVAS_WIDTH) {
        currentY += currentRowHeight;
        currentX = 0;
        currentRowHeight = 0;
      }
      slot.destX = currentX;
      slot.destY = currentY;
      currentX += ts;
      maxWidth = Math.max(maxWidth, currentX);
      currentRowHeight = Math.max(currentRowHeight, ts);
    }
    const canvasHeight = currentY + currentRowHeight;

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = maxWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    // Draw frames and build lookup tables
    const rotationLookup = new Map();
    const lpcAnimFrames = new Map(); // lpcAnim → Map(direction → frames[])

    for (const slot of frameSlots) {
      LPCCompositor._drawCompositeFrame(
        ctx, layers, layerImages, slot.lpcAnim, slot.direction,
        slot.frameIdx, slot.destX, slot.destY, slot.tileSize,
        lpcFrameCounts[slot.lpcAnim]
      );

      const frameData = {
        x: slot.destX,
        y: slot.destY,
        bgPosition: `-${slot.destX}px -${slot.destY}px`,
        tileSize: slot.tileSize
      };

      if (slot.type === 'rotation') {
        rotationLookup.set(slot.direction, frameData);
      } else {
        if (!lpcAnimFrames.has(slot.lpcAnim)) {
          lpcAnimFrames.set(slot.lpcAnim, new Map());
        }
        const dirMap = lpcAnimFrames.get(slot.lpcAnim);
        if (!dirMap.has(slot.direction)) {
          dirMap.set(slot.direction, []);
        }
        dirMap.get(slot.direction).push(frameData);
      }
    }

    // Build animationLookup: trixelAnim → dirMap
    // Multiple trixel anims that map to the same LPC anim share frame references (dedup)
    const animationLookup = new Map();
    for (const trixelAnim of trixelAnimations) {
      const lpcAnim = TRIXEL_TO_LPC_ANIM[trixelAnim] || trixelAnim;
      const dirMap = lpcAnimFrames.get(lpcAnim);
      if (dirMap) {
        animationLookup.set(trixelAnim, dirMap);
      }
    }

    const hasOversize = Object.values(animTileSizes).some(s => s > FRAME_SIZE);

    return {
      canvas,
      width: maxWidth,
      height: canvasHeight,
      rotationLookup,
      animationLookup,
      hasOversize
    };
  }

  /**
   * Draw a single composite frame (all layers stacked) at destX, destY.
   * tileSize controls the atlas tile dimensions — standard layers (64x64) get
   * centered within oversize tiles (192x192) so weapon arcs are preserved.
   */
  static _drawCompositeFrame(ctx, layers, layerImages, lpcAnim, trixelDir, frameIdx, destX, destY, tileSize = FRAME_SIZE, targetFrameCount = 0) {
    const dirInfo = DIRECTION_MAP[trixelDir];
    if (!dirInfo) return;

    const { lpcDir, flip } = dirInfo;
    const srcRow = LPC_DIR_ROWS[lpcDir];

    // Direction-specific draw order adjustments for tails, wings, shields.
    // Group values control relative ordering; within a group, normal zPos applies.
    let drawOrder;
    if (lpcDir === 'north') {
      // Viewed from behind:
      //  shield on character's front → behind everything (-1)
      //  tail extends toward viewer → on top of everything (1)
      drawOrder = layers.map((_, i) => i);
      drawOrder.sort((a, b) => {
        const la = layers[a], lb = layers[b];
        const ga = la.isTail ? 1 : la.isShield ? -1 : 0;
        const gb = lb.isTail ? 1 : lb.isShield ? -1 : 0;
        if (ga !== gb) return ga - gb;
        return la.zPos - lb.zPos;
      });
    } else if (lpcDir === 'east' || lpcDir === 'west') {
      // Side view: the near wing (fg, high zPos) covers the tail.
      // Only boost wing fg layers (zPos > 80); wing bg layers stay behind body.
      drawOrder = layers.map((_, i) => i);
      drawOrder.sort((a, b) => {
        const la = layers[a], lb = layers[b];
        const ga = (la.isWing && la.zPos > 80) ? 1 : 0;
        const gb = (lb.isWing && lb.zPos > 80) ? 1 : 0;
        if (ga !== gb) return ga - gb;
        return la.zPos - lb.zPos;
      });
    }

    if (flip) {
      ctx.save();
      ctx.translate(destX + tileSize, destY);
      ctx.scale(-1, 1);
    }

    for (let i = 0; i < layers.length; i++) {
      const li = drawOrder ? drawOrder[i] : i;
      const key = `${li}_${lpcAnim}`;
      const entry = layerImages.get(key);
      if (!entry) continue;

      const { img, sourceAnim } = entry;
      const { frameW: imgFrameW, frameH: imgFrameH, frameCount: actualFrameCount } =
        LPCCompositor._getImageFrameSize(img, sourceAnim);

      // Map frameIdx proportionally when source and target have different frame counts
      // (e.g., cape falls back from 8-frame run to 9-frame walk)
      let clampedIdx;
      if (lpcAnim === 'idle' && sourceAnim === 'walk' && actualFrameCount >= 3) {
        // When idle falls back to walk, use adjacent walk frames (1, 2) for smooth
        // idle instead of proportional mapping which jumps to opposite stride extreme
        // (frame 0 can look awkward for some weapons, so start from frame 1)
        clampedIdx = Math.min(frameIdx + 1, actualFrameCount - 1);
      } else if (targetFrameCount > 1 && actualFrameCount !== targetFrameCount) {
        clampedIdx = Math.min(
          Math.round(frameIdx * (actualFrameCount - 1) / (targetFrameCount - 1)),
          actualFrameCount - 1
        );
      } else {
        clampedIdx = Math.min(frameIdx, actualFrameCount - 1);
      }
      const srcX = clampedIdx * imgFrameW;
      let srcY = srcRow * imgFrameH;

      // Check bounds
      if (srcX + imgFrameW > img.width + 1) continue;
      if (srcY + imgFrameH > img.height) {
        srcY = 0;
        if (srcY + imgFrameH > img.height) continue;
      }

      // Center the source frame within the tile.
      // - 64x64 body layers in a 192x192 tile: centered with 64px offset each side
      // - 192x192 weapon layers in a 192x192 tile: fill the tile
      // - Layers larger than tile: center-crop to tile size
      const drawW = Math.min(imgFrameW, tileSize);
      const drawH = Math.min(imgFrameH, tileSize);
      const offsetX = Math.floor((tileSize - drawW) / 2);
      let offsetY = Math.floor((tileSize - drawH) / 2);
      const cropX = srcX + Math.max(0, Math.floor((imgFrameW - tileSize) / 2));
      const cropY = srcY + Math.max(0, Math.floor((imgFrameH - tileSize) / 2));

      if (flip) {
        ctx.drawImage(img, cropX, cropY, drawW, drawH, offsetX, offsetY, drawW, drawH);
      } else {
        ctx.drawImage(img, cropX, cropY, drawW, drawH, destX + offsetX, destY + offsetY, drawW, drawH);
      }
    }

    if (flip) {
      ctx.restore();
    }
  }

  /**
   * Determine the frame dimensions and count for a loaded spritesheet image.
   * Handles non-standard sheet sizes (e.g., 1664x512 katana) by scanning
   * all known LPC frame counts for one that produces a standard frame width.
   */
  static _getImageFrameSize(img, sourceAnim) {
    let fc = LPC_FRAME_COUNTS[sourceAnim] || LPC_FRAME_COUNTS[LPC_ANIM_FOLDER[sourceAnim]] || 2;
    let fw = Math.round(img.width / fc);
    let fh = Math.round(img.height / 4);

    if (fw !== 64 && fw !== 128 && fw !== 192) {
      let found = false;
      for (const count of Object.values(LPC_FRAME_COUNTS)) {
        const testFw = img.width / count;
        if (testFw === 64 || testFw === 128 || testFw === 192) {
          fw = testFw;
          fc = count;
          found = true;
          break;
        }
      }
      if (!found) {
        fw = img.width;
        fh = img.height >= FRAME_SIZE * 4 ? Math.round(img.height / 4) : img.height;
        fc = 1;
      }
    }

    // Clamp frame count to known animation count when sheet has padding.
    // walk_128 sheets are 1664px (13×128) but only 9 walk frames have content;
    // without this clamp, proportional mapping hits empty padding frames.
    const knownCount = LPC_FRAME_COUNTS[sourceAnim];
    if (knownCount && fc > knownCount) {
      fc = knownCount;
    }

    return { frameW: fw, frameH: fh, frameCount: fc };
  }

  /**
   * Load an image from a URL, with deduplication cache.
   * Caches the Promise so concurrent compose() calls for different characters
   * that share the same layer PNG/WebP only trigger one network fetch.
   */
  static _loadImage(src) {
    if (_imageCache.has(src)) {
      return _imageCache.get(src);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => {
        _imageCache.delete(src);
        reject(new Error(`Failed to load: ${src}`));
      };
      img.src = src;
    });

    // LRU eviction
    if (_imageCache.size >= MAX_IMAGE_CACHE_SIZE) {
      const oldestKey = _imageCache.keys().next().value;
      _imageCache.delete(oldestKey);
    }

    _imageCache.set(src, promise);
    return promise;
  }

  // _canvasToBlobUrlSync removed — compose() now creates blob URL directly
  // to avoid data URL size limits with large oversize atlases

  /**
   * Generate cache key from layer definitions
   */
  static _getCacheKey(layers, trixelAnimations) {
    const layerKey = layers.map(l => `${l.basePath}:${l.variant}:${l.zPos}`).join('|');
    const animKey = [...trixelAnimations].sort().join(',');
    return `${layerKey}#${animKey}`;
  }

  /**
   * Clear the entire atlas cache
   */
  static clearCache() {
    for (const atlas of _atlasCache.values()) {
      atlas._inCache = false;
      if (atlas._refCount <= 0) {
        atlas.destroy();
      }
    }
    _atlasCache.clear();
    _imageCache.clear();
  }

  /**
   * Get LPC animation name for a trixel animation
   */
  static getLPCAnim(trixelAnim) {
    return TRIXEL_TO_LPC_ANIM[trixelAnim] || trixelAnim;
  }

  /**
   * Get all supported trixel animation names
   */
  static getSupportedAnimations() {
    return Object.keys(TRIXEL_TO_LPC_ANIM);
  }
}

/**
 * LPCAtlas - SpriteAtlas-compatible object produced by LPCCompositor
 */
class LPCAtlas {
  constructor(options) {
    this.url = options.url;
    this._canvas = options.canvas;
    this.width = options.width;
    this.height = options.height;
    this.frameWidth = options.frameWidth;
    this.frameHeight = options.frameHeight;
    this.cols = options.cols;
    this.rows = options.rows;
    this.rotationLookup = options.rotationLookup;
    this.animationLookup = options.animationLookup;
    this.bodyFrameSize = options.bodyFrameSize || options.frameWidth;
    this.hasOversize = options.hasOversize || false;
    this.frameMap = new Map(); // compatibility

    // Reference count: tracks how many sprites are actively using this atlas.
    // Prevents blob URL revocation while any sprite still displays it.
    this._refCount = 0;

    // Async upgrade to blob URL for better performance
    this._upgradeToBlobUrl();
  }

  async _upgradeToBlobUrl() {
    // No-op: compose() now creates blob URL directly.
    // Kept for backward compatibility if external code calls it.
  }

  getAnimationFrame(animName, direction, frameIndex) {
    const dirMap = this.animationLookup.get(animName);
    if (!dirMap) return null;
    const frames = dirMap.get(direction);
    if (!frames || frameIndex < 0 || frameIndex >= frames.length) return null;
    return frames[frameIndex];
  }

  getFrameCount(animName, direction) {
    const dirMap = this.animationLookup.get(animName);
    if (!dirMap) return 0;
    const frames = dirMap.get(direction);
    return frames ? frames.length : 0;
  }

  getRotationFrame(direction) {
    return this.rotationLookup.get(direction) || null;
  }

  getCSSProperties() {
    return {
      backgroundImage: `url(${this.url})`,
      backgroundSize: `${this.width}px ${this.height}px`,
      width: `${this.frameWidth}px`,
      height: `${this.frameHeight}px`,
      imageRendering: 'pixelated'
    };
  }

  /** Increment reference count (called when a sprite starts using this atlas) */
  retain() {
    this._refCount++;
  }

  /** Decrement reference count and destroy if no longer referenced or cached */
  release() {
    this._refCount--;
    if (this._refCount <= 0 && !this._inCache) {
      this.destroy();
    }
  }

  destroy() {
    if (this.url && this.url.startsWith('blob:')) {
      URL.revokeObjectURL(this.url);
    }
    this.url = null;
    this.rotationLookup.clear();
    this.animationLookup.clear();
    this.frameMap.clear();
    this._canvas = null;
  }
}
